import { createFileRoute, useRouter } from "@tanstack/react-router";
import React, { useEffect } from "react";
import { useTranslation } from "react-i18next";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "src/components/base/table";
import { Badge } from "src/components/base/badges/base";
import { ApiCustomer } from "src/api/api_customer";
import TablePagination from "src/components/table-pagination";
import WS from "src/api/ws";
import { TagBadge } from "src/components/base/badges/tag";

/**
 * The properties for {@link ApprovedSubnetsOverview}
 */
export type ApprovedSubnetsOverviewProps = {};

const LIMIT = 25;

/**
 * Overview for all subnets
 */
function ApprovedSubnetsOverview(props: ApprovedSubnetsOverviewProps) {
    const [t] = useTranslation("customer-subnets");
    const [tg] = useTranslation();

    const { page } = Route.useSearch();
    const router = useRouter();

    const data = Route.useLoaderData();
    if (!data) {
        return;
    }

    useEffect(() => {
        const listener = WS.addEventListener("message.SubnetsChanged", () => {
            router.invalidate({ sync: true });
        });

        return () => WS.removeEventListener(listener);
    }, []);

    return (
        <div className={"flex flex-col gap-6"}>
            <Table dense={true}>
                <TableHead>
                    <TableRow>
                        <TableHeader>{t("label.prefix")}</TableHeader>
                        <TableHeader>{t("label.device-router")}</TableHeader>
                        <TableHeader>{t("label.description")}</TableHeader>
                    </TableRow>
                </TableHead>
                <TableBody>
                    {data.items.map((subnet) => (
                        <TableRow
                            key={subnet.uuid}
                            href={"/c/subnets/$subnetId/inbound-acls"}
                            params={{ subnetId: subnet.uuid }}
                        >
                            <TableCell>
                                <div className={"flex gap-5"}>
                                    <span>{subnet.name}</span>
                                    {!subnet.has_router && <Badge color={"teal"}>{tg("label.disabled")}</Badge>}
                                </div>
                            </TableCell>
                            <TableCell>
                                {subnet.router ? (
                                    <div className={"flex flex-col gap-1"}>
                                        <span>{subnet.router.dnsname}</span>
                                        <div className={"flex gap-1"}>
                                            {subnet.router.tags.map((tag) => (
                                                <TagBadge key={tag.uuid} tag={tag} />
                                            ))}
                                        </div>
                                    </div>
                                ) : (
                                    "-"
                                )}
                            </TableCell>
                            <TableCell className={"w-[50ch] text-wrap break-all whitespace-pre-wrap"}>
                                {subnet.description}
                            </TableCell>
                        </TableRow>
                    ))}
                </TableBody>
            </Table>

            <TablePagination
                currentPage={page}
                maxPages={data.total == 0 ? 1 : Math.ceil(data.total / LIMIT)}
                href={"/c/subnets/approved"}
                getSearchParams={(newPage: number) => ({ page: newPage })}
            />
        </div>
    );
}

/**
 * Type that defines the search parameter of this endpoint
 */
type ApprovedSubnetsOverviewSearch = {
    /** The current page number we're on */
    page: number;
};

export const Route = createFileRoute("/_customer/c/subnets/_subnets/approved")({
    component: ApprovedSubnetsOverview,
    validateSearch: (search: Record<string, unknown>): ApprovedSubnetsOverviewSearch => {
        const page = Number(search?.page ?? 1);

        return { page: page <= 0 ? 1 : page };
    },
    loaderDeps: ({ search: { page } }) => ({ page }),
    loader: async ({ deps }) =>
        await ApiCustomer.subnets.getAll({
            limit: LIMIT,
            offset: (deps.page - 1) * LIMIT,
        }),
});
